<template>
  <v-app id="app" v-if="userDataLoadedOrDontNeed">
    <div v-if="skipSecurity" class="skip-security-marker" />
    <TopToolbar :key="route.fullPath" />
    <Snackbar />
    <v-main :class="{ 'v-main--edu': isEduMode || isEducational }">
      <AdminMaintenanceBanner />
      <MaintenanceBanner />
      <v-container class="main-container">
        <RouterView />
      </v-container>
    </v-main>
    <confirm-dialog
      v-if="isEducational"
      :dialogVisible="proAdDialogVisible"
      :title="proAdHeader"
      :message="proAdText"
      :option="proAdOption"
      :confirmButton="proAdConfirmButton"
      @confirmClick="proAdConfirm"
      @cancelClick="proAdCancel"
    />
    <info-dialog
      v-if="isEducational"
      :dialogVisible="eduRegistrationCompleteDialogVisible"
      :title="eduRegistrationCompleteHeader"
      :message="eduRegistrationCompleteText"
      :maxWidth="500"
      @close="eduRegistrationCompleteDialogClosed"
    />
    <product-fruits
      v-if="isProductFruitsOn && user"
      project-code="ocblOqrTQ6B2jMXZ"
      language="en"
      :username="user.firstName + ' ' + user.lastName"
      :email="user.email"
      :firstName="user.firstName"
      :lastName="user.lastName"
      :sign-up-at="user.lastLoginDate"
      :role="role"
    />
  </v-app>
</template>

<script setup lang="ts">
import { onMounted, computed, onBeforeUnmount, ref } from 'vue';
import { useRoute, RouterView } from 'vue-router';
import store from '@/store';
import TopToolbar from '@/components/shared/common/MoaTopToolbar.vue';
import Snackbar from '@/components/shared/common/MoaSnackbar.vue';
import ConfirmDialog from '@/components/shared/dialogs/ConfirmDialog.vue';
import InfoDialog from '@/components/shared/dialogs/InfoDialog.vue';
import type { UserDto } from '@/castapi';
import ProductFruits from '@/components/shared/integrations/productFruits';
import MaintenanceBanner from '@/components/shared/common/MaintenanceBanner.vue';
import AdminMaintenanceBanner from '@/components/admin/Maintenance/AdminMaintenanceBanner.vue';
import { router, skipSecurity, toggleSkipSecurity } from '@/utils';

const route = useRoute();
const isEduMode = computed<boolean>(() => store.getters['adminEdu/isEduMode'] || 'edu' in route.query);
const urlsWithProductFruits = ['app.cast-soft.com' /*, 'develop.id.cast-soft.com'*/ /*, 'localhost'*/];
const user = computed<UserDto>(() => store.getters['login/user']);
const role = computed<undefined | string>(() => store.getters['login/roleName']);
const isProductFruitsOn = computed<boolean>(() =>
  urlsWithProductFruits.some(url => window.location.href.includes(url)),
);
const isEducational = computed<boolean>(() => store.getters['login/isEducational']);
const showProAd = computed<boolean>(() => store.getters['login/showProAd']);
const userActive = computed<boolean>(() => store.getters['login/userActive']);
const userDataLoadedOrDontNeed = ref(false);
const proAdDialogVisible = ref(false);
let unsubscribe: undefined | (() => void);

const proAdConfirm = (optionValue: boolean) => {
  proAdDialogVisible.value = false;
  if (optionValue) {
    store.dispatch('login/hideProAd');
  }
  router.push('/shop');
};

const proAdCancel = (optionValue: boolean) => {
  if (optionValue) {
    store.dispatch('login/hideProAd');
  }
  proAdDialogVisible.value = false;
};

// Ignore some debug messages
const originalConsoleLog = console.log;
console.log = (message: unknown, ...optionalParams: unknown[]) => {
  if (typeof message === 'string' && message.includes('-- pf - starting script detection')) {
    return;
  }
  originalConsoleLog.apply(console, [message, ...optionalParams]);
};

const onKeyDown = (e: KeyboardEvent) => {
  if (e.ctrlKey && e.altKey && e.code === 'KeyD') {
    toggleSkipSecurity();
  }
};

const proAdText =
  'You can upgrade to a professional license which removes all watermarks, at 50% off.<br/><br/>Would you like to do so now?';
const proAdHeader = 'Upgrade to Pro';
const proAdOption = 'Do not show again';
const proAdConfirmButton = { text: 'Yes', color: 'primary' };
const eduRegistrationCompleteText =
  'Thank you for registering! <br/>Your WYSIWYG Perform Lite Educational License has been approved and a confirmation email has been sent to you. <br/>Please open this email for information on how to proceed.';
const eduRegistrationCompleteHeader = 'Registration Complete';
const eduRegistrationCompleteDialogVisible = ref(false);

const eduRegistrationCompleteDialogClosed = () => {
  eduRegistrationCompleteDialogVisible.value = false;
  store.dispatch('login/GET_CURRENT_USER_DATA');
};
onMounted(async () => {
  store.dispatch('maintenance/startPolling');
  window.addEventListener('keydown', onKeyDown);
  unsubscribe = store.subscribe(mutation => {
    if (mutation.type === 'login/RELOAD_USER_DATA_SUCCESS') {
      if (store.getters['login/loggedIn']) {
        userDataLoadedOrDontNeed.value = true;
      }
    } else if (mutation.type === 'login/USER_REDIRECTED_AFTER_LOGIN') {
      proAdDialogVisible.value = isEducational.value && showProAd.value && userActive.value;
    } else if (mutation.type === 'login/PHONE_VERIFIED_FOR_EDU_REGISTRATION') {
      eduRegistrationCompleteDialogVisible.value = true;
    }
  });
  if (store.getters['login/loggedIn']) {
    await store.dispatch('login/RELOAD_CURRENT_USER_DATA');
  }
  if (!store.getters['login/loggedIn']) {
    userDataLoadedOrDontNeed.value = true;
  }
});
onBeforeUnmount(() => {
  store.dispatch('maintenance/stopPolling');
  window.removeEventListener('keydown', onKeyDown);
  if (unsubscribe) {
    unsubscribe();
  }
});
</script>

<style lang="scss">
@use './assets/scss/App.scss' as *;
</style>
